import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Gift, Download, ExternalLink, RefreshCw, Copy, Check, Users, MessageCircle, Sparkles, Globe, Lock, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import {
  getCapsuleGiftStats,
  generateCapsuleGift,
  getMyGift,
  getCapsuleMessageOwner,
  setCapsuleMessageApproval,
  getCapsuleGiftPdfUrl,
  type CapsuleGiftStats,
  type MemoryCapsuleGift,
} from '@/src/infrastructure/lib/api';

interface CapsuleGiftManagerProps {
  invitationId: string;
  birthdayPersonName?: string;
}

type Phase = 'loading' | 'no-messages' | 'pre-generate' | 'generating' | 'gift-ready' | 'error';

function StatPill({ icon, value, label, color }: { icon: React.ReactNode; value: number; label: string; color: string }) {
  return (
    <div className={`flex flex-col items-center justify-center rounded-2xl px-4 py-4 border ${color}`}>
      <div className="mb-1">{icon}</div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-white/50">{label}</p>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/10 hover:bg-white/15 border border-white/15 text-white/70 rounded-lg transition-colors"
    >
      {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function GenerateForm({
  defaultName,
  onGenerate,
  loading,
}: {
  defaultName: string;
  onGenerate: (payload: { recipientName: string; title: string; subtitle: string; isPublic: boolean }) => void;
  loading: boolean;
}) {
  const [recipientName, setRecipientName] = useState(defaultName);
  const [title, setTitle] = useState(`${defaultName}'s Memory Gift`);
  const [subtitle, setSubtitle] = useState('A collection of wishes from people who love you.');
  const [isPublic, setIsPublic] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (defaultName) {
      setRecipientName(defaultName);
      setTitle(`${defaultName}'s Memory Gift`);
    }
  }, [defaultName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate({ recipientName, title, subtitle, isPublic });
  };

  const inputClass =
    'w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-white placeholder:text-white/25 text-sm outline-none focus:border-purple-400/50 focus:ring-1 focus:ring-purple-400/20 transition-all';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-white/60 text-xs font-semibold uppercase tracking-wide block mb-1.5">
          Recipient Name *
        </label>
        <input
          className={inputClass}
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          placeholder="Birthday person's name"
          maxLength={100}
          required
        />
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 text-purple-400 text-xs font-semibold"
      >
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {expanded ? 'Hide' : 'Customize'} title &amp; subtitle
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden space-y-3"
          >
            <div>
              <label className="text-white/60 text-xs font-semibold uppercase tracking-wide block mb-1.5">
                Gift Title
              </label>
              <input
                className={inputClass}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Aarav's Birthday Memory Book"
                maxLength={200}
              />
            </div>
            <div>
              <label className="text-white/60 text-xs font-semibold uppercase tracking-wide block mb-1.5">
                Subtitle
              </label>
              <input
                className={inputClass}
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="e.g. A collection of wishes from people who love you."
                maxLength={300}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Public toggle */}
      <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-4 py-3">
        <div>
          <p className="text-white text-sm font-semibold flex items-center gap-2">
            {isPublic ? <Globe size={14} className="text-green-400" /> : <Lock size={14} className="text-orange-400" />}
            {isPublic ? 'Public link' : 'Private (token required)'}
          </p>
          <p className="text-white/40 text-xs">
            {isPublic
              ? 'Anyone with the link can open the gift'
              : 'Only accessible via the private share token'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsPublic((v) => !v)}
          className={`relative w-10 h-6 rounded-full transition-colors ${isPublic ? 'bg-green-500' : 'bg-white/20'}`}
        >
          <span
            className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isPublic ? 'left-5' : 'left-1'}`}
          />
        </button>
      </div>

      <button
        type="submit"
        disabled={loading || !recipientName.trim()}
        className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-sm rounded-2xl hover:from-purple-400 hover:to-pink-400 transition-all shadow-lg shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white"
            />
            Generating Gift…
          </>
        ) : (
          <>
            <Sparkles size={15} />
            Generate Memory Gift
          </>
        )}
      </button>
    </form>
  );
}

interface OwnerMessage {
  id: string;
  senderName: string;
  message: string;
  createdAt: string;
  approved: boolean;
}

function MessageReviewList({
  invitationId,
  messages,
  onApprovalChange,
}: {
  invitationId: string;
  messages: OwnerMessage[];
  onApprovalChange: () => void;
}) {
  const [toggling, setToggling] = useState<string | null>(null);

  const handleToggle = async (msg: OwnerMessage) => {
    if (toggling) return;
    setToggling(msg.id);
    try {
      await setCapsuleMessageApproval(msg.id, !msg.approved);
      onApprovalChange();
    } catch {
      // ignore
    } finally {
      setToggling(null);
    }
  };

  return (
    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`flex items-start gap-3 rounded-xl px-3 py-2.5 border transition-colors ${
            msg.approved ? 'bg-white/5 border-white/10' : 'bg-white/2 border-white/5 opacity-50'
          }`}
        >
          <div className="flex-1 min-w-0">
            <p className="text-purple-300 text-xs font-bold">{msg.senderName}</p>
            <p className="text-white/70 text-xs leading-relaxed line-clamp-2">{msg.message}</p>
          </div>
          <button
            onClick={() => handleToggle(msg)}
            disabled={toggling === msg.id}
            className="shrink-0 mt-0.5"
            title={msg.approved ? 'Hide from gift' : 'Include in gift'}
          >
            {msg.approved
              ? <Eye size={15} className="text-green-400 hover:text-red-400 transition-colors" />
              : <EyeOff size={15} className="text-white/30 hover:text-green-400 transition-colors" />
            }
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function CapsuleGiftManager({ invitationId, birthdayPersonName = '' }: CapsuleGiftManagerProps) {
  const [stats, setStats] = useState<CapsuleGiftStats | null>(null);
  const [gift, setGift] = useState<MemoryCapsuleGift | null>(null);
  const [messages, setMessages] = useState<OwnerMessage[]>([]);
  const [phase, setPhase] = useState<Phase>('loading');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [showMessages, setShowMessages] = useState(false);

  const loadData = async () => {
    try {
      setPhase('loading');
      const s = await getCapsuleGiftStats(invitationId);
      setStats(s);

      const msgs = await getCapsuleMessageOwner(invitationId);
      setMessages(msgs as OwnerMessage[]);

      if (s.totalMessages === 0) {
        setPhase('no-messages');
        return;
      }

      if (s.hasGift) {
        const g = await getMyGift(invitationId);
        setGift(g);
        setPhase('gift-ready');
      } else {
        setPhase('pre-generate');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load capsule data');
      setPhase('error');
    }
  };

  useEffect(() => { loadData(); }, [invitationId]);

  const handleGenerate = async (payload: {
    recipientName: string;
    title: string;
    subtitle: string;
    isPublic: boolean;
  }) => {
    setGenerating(true);
    setError('');
    try {
      const g = await generateCapsuleGift(invitationId, payload);
      setGift(g);
      setPhase('gift-ready');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate gift');
    } finally {
      setGenerating(false);
    }
  };

  const giftUrl = gift
    ? `${window.location.origin}/gift/${gift.giftSlug}`
    : null;

  const giftTokenUrl = stats?.shareToken
    ? `${window.location.origin}/gift/${gift?.giftSlug ?? ''}?token=${stats.shareToken}`
    : null;

  const pdfUrl = gift ? getCapsuleGiftPdfUrl(gift.giftSlug) : '#';

  return (
    <div
      className="rounded-3xl border border-purple-400/20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0f172a 0%, #150e2a 100%)' }}
    >
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-white/5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center">
            <Gift size={18} className="text-purple-300" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">Memory Capsule Gift</h3>
            <p className="text-white/40 text-xs">Transform messages into an interactive digital gift</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-5">
        <AnimatePresence mode="wait">

          {/* Loading */}
          {phase === 'loading' && (
            <motion.div key="loading" className="flex items-center justify-center py-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-8 h-8 rounded-full border-2 border-purple-400/30 border-t-purple-400"
              />
            </motion.div>
          )}

          {/* No messages yet */}
          {phase === 'no-messages' && (
            <motion.div
              key="no-messages"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8"
            >
              <p className="text-3xl mb-3">💌</p>
              <p className="text-white font-semibold">No messages yet</p>
              <p className="text-white/40 text-sm mt-1">
                Once guests leave Memory Capsule messages on the invitation, you can generate a gift here.
              </p>
            </motion.div>
          )}

          {/* Pre-generate: stats + form */}
          {(phase === 'pre-generate' || generating) && stats && (
            <motion.div
              key="pre-generate"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-5"
            >
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <StatPill
                  icon={<MessageCircle size={16} className="text-purple-300" />}
                  value={stats.totalMessages}
                  label="Messages"
                  color="border-purple-400/20 bg-purple-500/10"
                />
                <StatPill
                  icon={<Users size={16} className="text-pink-300" />}
                  value={stats.totalSenders}
                  label="Contributors"
                  color="border-pink-400/20 bg-pink-500/10"
                />
              </div>

              {/* Message review toggle */}
              {messages.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowMessages((v) => !v)}
                    className="flex items-center gap-1.5 text-white/50 text-xs font-semibold mb-2 hover:text-white/70 transition-colors"
                  >
                    {showMessages ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {showMessages ? 'Hide' : 'Review'} messages ({messages.length})
                  </button>
                  <AnimatePresence>
                    {showMessages && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <MessageReviewList
                          invitationId={invitationId}
                          messages={messages}
                          onApprovalChange={loadData}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              <GenerateForm
                defaultName={birthdayPersonName}
                onGenerate={handleGenerate}
                loading={generating}
              />

              {error && <p className="text-red-300 text-xs font-semibold">{error}</p>}
            </motion.div>
          )}

          {/* Gift ready */}
          {phase === 'gift-ready' && gift && stats && (
            <motion.div
              key="gift-ready"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-5"
            >
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <StatPill
                  icon={<MessageCircle size={16} className="text-purple-300" />}
                  value={gift.totalMessages}
                  label="Messages in Gift"
                  color="border-purple-400/20 bg-purple-500/10"
                />
                <StatPill
                  icon={<Users size={16} className="text-pink-300" />}
                  value={gift.totalSenders}
                  label="Contributors"
                  color="border-pink-400/20 bg-pink-500/10"
                />
              </div>

              {/* Gift title */}
              <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wide mb-0.5">Gift Title</p>
                <p className="text-white font-semibold text-sm">{gift.title}</p>
                <p className="text-white/40 text-xs mt-0.5">{gift.subtitle}</p>
              </div>

              {/* Share links */}
              <div className="space-y-2">
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wide">Share Gift</p>

                {gift.isPublic && giftUrl && (
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
                    <Globe size={13} className="text-green-400 shrink-0" />
                    <span className="text-white/50 text-xs truncate flex-1">{giftUrl}</span>
                    <CopyButton text={giftUrl} />
                    <a
                      href={giftUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-white/40 hover:text-white transition-colors"
                    >
                      <ExternalLink size={13} />
                    </a>
                  </div>
                )}

                {giftTokenUrl && (
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
                    <Lock size={13} className="text-orange-400 shrink-0" />
                    <span className="text-white/50 text-xs truncate flex-1">
                      Private token link
                    </span>
                    <CopyButton text={giftTokenUrl} />
                    <a
                      href={giftTokenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-white/40 hover:text-white transition-colors"
                    >
                      <ExternalLink size={13} />
                    </a>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={gift.isPublic && giftUrl ? giftUrl : giftTokenUrl ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 py-2.5 bg-purple-500/20 border border-purple-400/30 text-purple-300 text-sm font-semibold rounded-xl hover:bg-purple-500/30 transition-colors"
                >
                  <ExternalLink size={13} />
                  Preview
                </a>
                <a
                  href={pdfUrl}
                  download
                  className="flex items-center justify-center gap-1.5 py-2.5 bg-white/10 border border-white/15 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-colors"
                >
                  <Download size={13} />
                  Export PDF
                </a>
              </div>

              {/* Message review */}
              {messages.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowMessages((v) => !v)}
                    className="flex items-center gap-1.5 text-white/40 text-xs font-semibold mb-2 hover:text-white/60 transition-colors"
                  >
                    {showMessages ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {showMessages ? 'Hide' : 'Curate'} messages
                  </button>
                  <AnimatePresence>
                    {showMessages && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <MessageReviewList
                          invitationId={invitationId}
                          messages={messages}
                          onApprovalChange={loadData}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Regenerate */}
              <button
                onClick={() => setPhase('pre-generate')}
                className="flex items-center gap-1.5 text-white/30 text-xs hover:text-white/50 transition-colors"
              >
                <RefreshCw size={12} />
                Regenerate Gift
              </button>
            </motion.div>
          )}

          {/* Error */}
          {phase === 'error' && (
            <motion.div key="error" className="text-center py-6">
              <p className="text-red-300 text-sm">{error}</p>
              <button onClick={loadData} className="mt-3 text-purple-300 text-xs underline">
                Retry
              </button>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}
